import { createQueryMatcherRetention } from '../utils/cache/query-matcher-retention.util';
import type { QueryClient } from '@tanstack/react-query';
import {
  CrmFunnelsConversationsService,
  SearcherDocumentSearchFilterOperator,
  type FunnelConversation,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { ApiError } from 'src/api/generated/core/ApiError';

import { hasDeletedMessagePreview } from '../utils/cache/funnel-conversations/deleted-message-preview.util';
import { refreshConversationSearch } from './conversation-search-refresh.service';
import { scheduleBackgroundSearch } from './background-search.service';
import type {
  ConversationSearchMatcher,
  ConversationSearchScope,
} from '../types/conversation-search-refresh.types';
import type { ConversationDocumentRecoveryTarget } from '../types/conversation-document-recovery.types';

const DOCUMENT_QUERY_KEY = ['crm/documents/funnel-conversations'];
const RECOVERY_WINDOW_MS = 30_000;
const REQUEST_TIMEOUT_MS = 5_000;
const targets = new WeakMap<
  QueryClient,
  Map<string, ConversationDocumentRecoveryTarget>
>();

const hasActiveDocuments = (
  queryClient: QueryClient,
  scope: ConversationSearchScope
): boolean =>
  [...scope].some(
    (query) =>
      query.isActive() &&
      queryClient.getQueryCache().get(query.queryHash) === query
  );

const hasIndexedRevision = (
  document: FunnelConversationDocument,
  conversation: FunnelConversation
): boolean =>
  document.id === String(conversation.id) &&
  document.tenantId === conversation.tenantId &&
  Date.parse(document.updatedAt) >= Date.parse(conversation.updatedAt) &&
  String(document.funnelId ?? '') === String(conversation.funnelId) &&
  String(document.funnelStatusId ?? '') ===
    String(conversation.funnelStatusId ?? '') &&
  String(document.userId ?? '') === String(conversation.userId ?? '') &&
  String(document.propertyId ?? '') === String(conversation.propertyId ?? '') &&
  (document.archivedAt ?? null) === (conversation.archivedAt ?? null);

/**
 * Refreshes active search views once indexing reaches the latest canonical state.
 * React Query coalesces concurrent requests for one tenant/conversation. This
 * best-effort recovery never fabricates search rows or replaces polling recovery.
 *
 * @param queryClient - Cache owning the canonical conversation and search views.
 * @param conversation - Accepted canonical event that requires search recovery.
 * @param scope - Affected cache instances; omitted only for broad recovery.
 * @param matchesNewQuery - Event-time predicate for filters opened during recovery.
 * @returns Completion of this attempt, or immediately when recovery already runs.
 */
export const recoverConversationDocuments = async (
  queryClient: QueryClient,
  conversation: FunnelConversation,
  scope: ConversationSearchScope = new Set(
    queryClient.getQueryCache().findAll({ queryKey: DOCUMENT_QUERY_KEY })
  ),
  matchesNewQuery?: ConversationSearchMatcher
): Promise<void> => {
  if (!hasActiveDocuments(queryClient, scope) && !matchesNewQuery) return;
  const queryKey = [
    'crm/conversation-document-recovery',
    conversation.tenantId,
    conversation.id,
  ];
  const state = queryClient.getQueryState(queryKey);
  let ownedTargets = targets.get(queryClient);
  if (!ownedTargets) {
    ownedTargets = new Map();
    targets.set(queryClient, ownedTargets);
  }
  const identity = `${conversation.tenantId}:${conversation.id}`;
  const currentTarget = ownedTargets.get(identity);
  if (state && state.fetchStatus !== 'idle' && currentTarget) {
    if (matchesNewQuery) currentTarget.newQueryMatchers.add(matchesNewQuery);
    scope.forEach((query) => currentTarget.scope.add(query));
    if (
      Date.parse(conversation.updatedAt) >=
      Date.parse(currentTarget.conversation.updatedAt)
    ) {
      currentTarget.conversation = conversation;
    }
    return;
  }
  const target: ConversationDocumentRecoveryTarget = {
    newQueryMatchers: createQueryMatcherRetention(),
    conversation,
    scope: new Set(scope),
  };
  if (matchesNewQuery) target.newQueryMatchers.add(matchesNewQuery);
  ownedTargets.set(identity, target);
  // Keep first-instance ownership for the whole indexed-recovery window, not
  // just the final 600 ms refresh. A removed key must not admit its replacement.
  const observedKeys = new Set(
    queryClient
      .getQueryCache()
      .findAll({ queryKey: DOCUMENT_QUERY_KEY })
      .map((query) => query.queryHash)
  );
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'added') return;
    const query = queryClient.getQueryCache().get(event.query.queryHash);
    if (!query || query.queryKey[0] !== DOCUMENT_QUERY_KEY[0]) return;
    if (observedKeys.has(query.queryHash)) return;
    observedKeys.add(query.queryHash);
    if (target.newQueryMatchers.matches(query)) {
      target.scope.add(query);
    }
  });
  const isRelevant = (): boolean =>
    ownedTargets.get(identity) === target &&
    hasActiveDocuments(queryClient, target.scope);
  const deadline = Date.now() + RECOVERY_WINDOW_MS;
  try {
    await queryClient.fetchQuery({
      queryKey,
      gcTime: 0,
      staleTime: 0,
      retry: (failures, error) =>
        failures < 5 &&
        Date.now() < deadline &&
        isRelevant() &&
        !(error instanceof ApiError && [401, 403].includes(error.status)),
      retryDelay: (attempt) =>
        Math.min(
          1_000 * 2 ** attempt,
          8_000,
          Math.max(0, deadline - Date.now())
        ),
      queryFn: async ({ signal }) => {
        const isAborted = (): boolean => signal.aborted;
        // Inactive affected views may gain a matching observer during the same
        // initial batching window. Do not spend an HTTP slot until one joins.
        if (!isRelevant() && matchesNewQuery && !signal.aborted) {
          await new Promise<void>((resolve) => {
            const finish = (): void => {
              window.clearTimeout(timer);
              signal.removeEventListener('abort', finish);
              resolve();
            };
            const timer = window.setTimeout(finish, 600);
            signal.addEventListener('abort', finish, { once: true });
          });
        }
        if (signal.aborted || !isRelevant() || Date.now() >= deadline) {
          return false;
        }
        const response = await scheduleBackgroundSearch(queryClient, {
          signal,
          deadline,
          isRelevant,
          run: async () => {
            const request =
              CrmFunnelsConversationsService.searchFunnelConversations({
                filters: [
                  {
                    field: 'id',
                    operator: SearcherDocumentSearchFilterOperator.EQ,
                    value: String(conversation.id),
                  },
                ],
                size: 1,
                includeArchived: true,
              });
            const cancel = () => request.cancel();
            signal.addEventListener('abort', cancel, { once: true });
            const timeout = window.setTimeout(
              cancel,
              Math.min(REQUEST_TIMEOUT_MS, deadline - Date.now())
            );
            try {
              return await request;
            } finally {
              window.clearTimeout(timeout);
              signal.removeEventListener('abort', cancel);
            }
          },
        });
        // Release the HTTP slot before awaiting a queued board refresh.
        if (!response || isAborted()) return false;
        const cached = queryClient.getQueryData<FunnelConversation>([
          'crm/funnel-conversations',
          conversation.id,
        ]);
        if (!cached || cached.tenantId !== conversation.tenantId) {
          return false;
        }
        // A late HTTP result must never lower this accepted event's target.
        const latest =
          Date.parse(cached.updatedAt) <
          Date.parse(target.conversation.updatedAt)
            ? target.conversation
            : cached;
        target.conversation = latest;
        if (
          !response.items.some(
            (item) =>
              hasIndexedRevision(item, latest) &&
              !hasDeletedMessagePreview(queryClient, item)
          )
        ) {
          throw new Error('Conversation search index has not caught up');
        }
        if (isRelevant()) {
          await refreshConversationSearch(
            queryClient,
            new Set(target.scope),
            (query) => target.scope.has(query)
          );
        }
        const current = queryClient.getQueryData<FunnelConversation>([
          'crm/funnel-conversations',
          conversation.id,
        ]);
        if (
          (current && current.updatedAt !== latest.updatedAt) ||
          target.conversation.updatedAt !== latest.updatedAt
        ) {
          throw new Error('Conversation advanced during search recovery');
        }
        return true;
      },
    });
  } catch {
    // Optional recovery must not surface external errors or undo accepted state.
    // Existing polling, visibility and reconnect recovery remain available.
  } finally {
    unsubscribe();
    target.newQueryMatchers.clear();
    if (ownedTargets.get(identity) === target) ownedTargets.delete(identity);
  }
};
